import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";

interface Props {
  open: boolean;
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  variant?: "primary" | "danger";
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  // Optional third action (e.g. "Save without notifying").
  secondaryLabel?: string;
  onSecondary?: () => void;
}

// Small confirm dialog, same shell as DraftReportModal / DeleteInvestigationModal.
// Opened with showModal() so it joins the browser's top layer: callers like
// AgencySurveyTab sit inside a dialog that was itself opened with
// showModal(), and no z-index can paint above the top layer. Portaled to
// <body> too, so a transformed ancestor (DaisyUI's modal-box) never traps it.
const ConfirmModal: React.FC<Props> = ({ open, title, body, confirmLabel, variant = "primary", busy, onConfirm, onClose, secondaryLabel, onSecondary }) => {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (open && el && !el.open) el.showModal();
  }, [open]);

  if (!open) return null;

  return createPortal(
    <dialog
      ref={ref}
      className="modal"
      // Escape fires "cancel" on the topmost dialog only, so the parent
      // modal stays open. Close through our own state, never natively.
      onCancel={(e) => {
        e.preventDefault();
        if (!busy) onClose();
      }}
    >
      <div className="modal-box max-w-md p-0 overflow-hidden rounded-2xl">
        <div className="flex items-start gap-3 px-6 pt-6 pb-4">
          <div className={variant === "danger" ? "size-10 shrink-0 rounded-xl bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 flex items-center justify-center" : "size-10 shrink-0 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 flex items-center justify-center"}>
            <ExclamationTriangleIcon className="size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-base-content leading-tight">{title}</h3>
            <div className="text-sm text-base-content/60 mt-1">{body}</div>
          </div>
          <button type="button" onClick={onClose} disabled={busy} className="p-1 rounded-md text-base-content/50 hover:bg-base-200 hover:text-base-content" title="Close">
            <XMarkIcon className="size-5" />
          </button>
        </div>
        <div className="px-6 pb-6 flex items-center justify-end gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {secondaryLabel && onSecondary && (
            <Button variant="secondary" size="sm" onClick={onSecondary} disabled={busy}>
              {secondaryLabel}
            </Button>
          )}
          <Button variant={variant} size="sm" onClick={onConfirm} disabled={busy}>
            {busy ? "Working…" : confirmLabel}
          </Button>
        </div>
      </div>
      <button type="button" className="modal-backdrop" onClick={() => !busy && onClose()} aria-label="Close" />
    </dialog>,
    document.body
  );
};

export default ConfirmModal;
